import {
  HHAlert,
  HHBox,
  HHGrid,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { ReactNode } from 'react';
import {
  ASSESSMENT_MATCHED_LABEL,
  ASSESSMENT_NOT_MATCHED_LABEL,
} from '../constants/partner-data-gateway.constant';
import { ChildWorkflowSummary, IngestionDetail } from '../domain/types';
import { useIngestionAssessments } from '../hooks/use-ingestion-assessments';
import { formatCount } from '../utils/format.utils';
import { printDateAndTime } from '../utils/print-date-and-time';
import { IdentityResolutionSection } from './identity-resolution-section';
import { JsonViewer } from './json-viewer';
import { ProgressBar } from './progress-bar';
import { SectionTitle, StatRow } from './stat-row';
import { SurfaceCard } from './surface-card';

export const OVERVIEW_TAB_TEST_ID = 'ingestion-overview-tab';
export const KEY_STATS_CARD_TEST_ID = 'key-stats-card';
export const ASSESSMENT_VERDICTS_CARD_TEST_ID = 'assessment-verdicts-card';
export const CHILD_WORKFLOW_CARD_TEST_ID = 'child-workflow-card';
export const ASSESSMENT_VERDICTS_ERROR_TEST_ID = 'assessment-verdicts-error';

interface NestedCardProps {
  testId: string;
  title: string;
  children: ReactNode;
}

const NestedCard = ({
  testId,
  title,
  children,
}: NestedCardProps): JSX.Element => (
  <SurfaceCard isNested paddingX={5} paddingY={4} data-testid={testId}>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </SurfaceCard>
);

const KeyStatsCard = ({
  ingestion,
}: {
  ingestion: IngestionDetail;
}): JSX.Element => (
  <NestedCard testId={KEY_STATS_CARD_TEST_ID} title="Key Stats">
    <HHBox marginTop={3} marginBottom={1}>
      <ProgressBar
        completed={ingestion.completedBatches}
        total={ingestion.totalBatches}
        testId="key-stats-batches-progress"
      />
    </HHBox>
    <HHStack>
      <StatRow label="Total Batches" value={ingestion.totalBatches} />
      <StatRow label="Completed Batches" value={ingestion.completedBatches} />
      <StatRow
        label="Total Rows Parsed"
        value={formatCount(ingestion.totalRowsParsed)}
      />
      {ingestion.liveProgress && (
        <StatRow
          label="Live Progress"
          value={`${ingestion.liveProgress.percentComplete}%`}
        />
      )}
    </HHStack>
  </NestedCard>
);

const AssessmentVerdictsCard = ({
  eventId,
}: {
  eventId: string;
}): JSX.Element => {
  const { assessments, isLoading, hasError } = useIngestionAssessments(eventId);

  return (
    <NestedCard
      testId={ASSESSMENT_VERDICTS_CARD_TEST_ID}
      title="Assessment Verdicts"
    >
      {isLoading && (
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="text.secondary"
          sx={{ marginTop: 2 }}
        >
          Loading assessments…
        </HHTypography>
      )}

      {/* testid sits on the wrapper: HHAlert does not forward unknown
          props to the DOM. */}
      {!isLoading && hasError && (
        <HHBox marginTop={2} data-testid={ASSESSMENT_VERDICTS_ERROR_TEST_ID}>
          <HHAlert hhVariant="outlined" severity="error">
            Could not load assessment verdicts. Please try again.
          </HHAlert>
        </HHBox>
      )}

      {!isLoading && !hasError && assessments.length === 0 && (
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="text.secondary"
          sx={{ marginTop: 2 }}
        >
          No assessments found.
        </HHTypography>
      )}

      {assessments.map(assessment => (
        <HHBox key={assessment.id} marginTop={2}>
          <HHBox display="flex" alignItems="center" gap={1}>
            {/* MUI icons are aria-hidden by default, so the verdict needs a
                text alternative — `titleAccess` renders an SVG <title>. */}
            {assessment.matched ? (
              <CheckCircle
                color="success"
                fontSize="small"
                titleAccess={ASSESSMENT_MATCHED_LABEL}
              />
            ) : (
              <Cancel
                color="error"
                fontSize="small"
                titleAccess={ASSESSMENT_NOT_MATCHED_LABEL}
              />
            )}
            <HHTypography hhVariant="variant-bypass" variant="body1">
              {assessment.configurationName}
            </HHTypography>
          </HHBox>
          <JsonViewer value={assessment.report} label="View report" />
        </HHBox>
      ))}
    </NestedCard>
  );
};

const ChildWorkflowCard = ({
  childWorkflow,
}: {
  childWorkflow: ChildWorkflowSummary;
}): JSX.Element => (
  <NestedCard testId={CHILD_WORKFLOW_CARD_TEST_ID} title="Child Workflow">
    <HHStack marginTop={1}>
      <StatRow label="Status" value={childWorkflow.status} />
      <StatRow label="Workflow ID" value={childWorkflow.workflowId} />
      <StatRow
        label="Started At"
        value={printDateAndTime(childWorkflow.startedAt)}
      />
      {childWorkflow.endedAt && (
        <StatRow
          label="Ended At"
          value={printDateAndTime(childWorkflow.endedAt)}
        />
      )}
      {/* PDGS only populates `progress` while the child workflow is
          RUNNING; it is null once the child finishes. */}
      {childWorkflow.progress && (
        <>
          <StatRow
            label="Resolved"
            value={childWorkflow.progress.resolvedCount}
          />
          <StatRow
            label="Pending"
            value={childWorkflow.progress.pendingCount}
          />
          {childWorkflow.progress.cachedCount != null && (
            <StatRow
              label="Cached"
              value={childWorkflow.progress.cachedCount}
            />
          )}
        </>
      )}
    </HHStack>
  </NestedCard>
);

interface OverviewTabProps {
  ingestion: IngestionDetail;
}

export const OverviewTab = ({ ingestion }: OverviewTabProps): JSX.Element => (
  <HHGrid container spacing={4} data-testid={OVERVIEW_TAB_TEST_ID}>
    <HHGrid item xs={12} md={6}>
      <KeyStatsCard ingestion={ingestion} />
    </HHGrid>
    <HHGrid item xs={12} md={6}>
      <AssessmentVerdictsCard eventId={ingestion.eventId} />
    </HHGrid>
    {ingestion.childWorkflow && (
      <HHGrid item xs={12}>
        <ChildWorkflowCard childWorkflow={ingestion.childWorkflow} />
      </HHGrid>
    )}
    <HHGrid item xs={12}>
      <IdentityResolutionSection ingestion={ingestion} />
    </HHGrid>
  </HHGrid>
);
